import { apiClient, API_URL } from "./apiConfig";
import { SESSION_ID } from "./apiConfig";
import { CACHE_DURATION_MS } from "../constants/gameMasterConstants";

export const GM_MEMBER_MILESTONE_STATUSES = [
	"unstarted",
	"started",
	"completed",
	"rewarded",
];

function gmMemberMilestoneErrorFromAxios(err, fallback) {
	const d = err?.response?.data;
	if (d && typeof d === "object" && d.success === false) {
		return d?.error?.detail || d?.message || fallback;
	}
	return err?.message || fallback;
}

/**
 * Backend: GET /api/goodglows/gmMemberMilestones/?targetMemberID=<int>
 * Session required; caller must be a gamemaster for a contest that includes the target member.
 */
export async function getGmMemberMilestones(targetMemberId) {
	const id = Number(targetMemberId);
	if (!Number.isFinite(id) || id <= 0) {
		throw new Error(
			"getGmMemberMilestones: targetMemberId must be a positive number"
		);
	}
	try {
		const response = await apiClient.get("/gmMemberMilestones/", {
			params: { targetMemberID: id },
		});
		const payload = response.data;
		if (payload?.success === false) {
			const detail =
				payload?.error?.detail || payload?.message || "Request failed";
			throw new Error(detail);
		}
		return payload?.result ?? payload;
	} catch (err) {
		throw new Error(gmMemberMilestoneErrorFromAxios(err, "Request failed"));
	}
}

/**
 * Backend: POST /api/goodglows/updateMilestone/ (cross-member requires GM + shared contest).
 */
export async function gmUpdateMemberMilestone(memberMilestoneId, status) {
	const mid = Number(memberMilestoneId);
	if (!Number.isFinite(mid) || mid <= 0) {
		throw new Error("gmUpdateMemberMilestone: invalid memberMilestoneId");
	}
	const s = String(status || "").toLowerCase();
	if (!GM_MEMBER_MILESTONE_STATUSES.includes(s)) {
		throw new Error(
			`gmUpdateMemberMilestone: status must be one of ${GM_MEMBER_MILESTONE_STATUSES.join(", ")}`
		);
	}
	try {
		const response = await apiClient.post("/updateMilestone/", {
			membermilestoneid: mid,
			status: s,
		});
		const payload = response.data;
		if (payload?.success === false) {
			const detail =
				payload?.error?.detail || payload?.message || "Update failed";
			throw new Error(detail);
		}
		return payload?.result ?? payload;
	} catch (err) {
		throw new Error(gmMemberMilestoneErrorFromAxios(err, "Update failed"));
	}
}

// Cache for contest info to prevent duplicate API calls
const contestInfoCache = new Map();
// Track ongoing requests to prevent duplicate simultaneous calls
const ongoingRequests = new Map();

/**
 * API service for authentication functionality
 */
export const gameMasterAPI = {
	getGroupChatMessages: async (
		contestID = 0,
		type = "single",
		partyID = 0,
		MemberID = 0
	) => {
		const response = await apiClient.get(`/getChatMessages/`, {
			params: {
				sessionid: SESSION_ID(),
				contestID,
				type,
				partyID,
				memberID: MemberID,
			},
		});
		return response.data;
	},

	getSingleChatMessages: async (receiver) => {
		const params = {
			sessionid: SESSION_ID(),
			recieverID: receiver,
		};

		const response = await apiClient.get(`/getSingleChatMessages/`, { params });
		return response.data;
	},

	SendMessage: async (groupID, chatmessage) => {
		const response = await apiClient.post(`/sendGroupChatMessage/`, {
			sessionid: SESSION_ID(),
			groupid: groupID,
			chatmessage,
		});
		return response.data;
	},

	getJudges: async (contestID) => {
		const response = await apiClient.get(`/getjudgesforcontest/`, {
			params: {
				contestid: contestID,
			},
		});
		return response;
	},

	getContestMembers: async (contestID) => {
		const response = await apiClient.get(`/getmembersforcontest/`, {
			params: {
				contestid: contestID,
			},
		});
		return response;
	},

	removeContestMember: async (contestID, memberID) => {
		const response = await apiClient.post(`/removecontestmember/`, {
			contestid: contestID,
			memberid: memberID,
		});
		return response;
	},

	getContestInfo: async (contestID) => {
		// Check cache first
		const cacheKey = `contest_${contestID}`;
		const cached = contestInfoCache.get(cacheKey);
		if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
			return cached.data;
		}

		// Check if there's already an ongoing request for this contest
		const ongoingRequest = ongoingRequests.get(cacheKey);
		if (ongoingRequest) {
			return await ongoingRequest;
		}

		// Create a promise for this request and store it
		const requestPromise = apiClient
			.get(`/getcontestinfo/`, {
				params: {
					contestid: contestID,
				},
			})
			.then((response) => {
				// Cache the result
				contestInfoCache.set(cacheKey, {
					data: response.data,
					timestamp: Date.now(),
				});
				// Remove from ongoing requests
				ongoingRequests.delete(cacheKey);
				return response.data;
			})
			.catch((error) => {
				// Remove from ongoing requests even on error
				ongoingRequests.delete(cacheKey);
				throw error;
			});

		// Store the ongoing request
		ongoingRequests.set(cacheKey, requestPromise);

		return await requestPromise;
	},
	postJudgement: async (
		queueItemID,
		contestID,
		lumens = 0,
		score = 0,
		comment = "",
		approve = 1
	) => {
		const response = await apiClient.post(`/postjudgement/`, {
			sessionid: SESSION_ID(),
			contestid: contestID,
			comment,
			queueitemid: queueItemID,
			score,
			lumens,
			approve,
		});
		return response.data;
	},

	getJudgeQueue: async (contestID, MemberID, index) => {
		const response = await apiClient.get(`/getjudgingqueue/`, {
			params: {
				contestid: contestID,
				memberid: MemberID,
				index,
			},
		});
		return response.data;
	},

	getNextJudgeQueueItem: async (
		contestID,
		currentQueueItemID = 0,
		direction = "next"
	) => {
		const response = await apiClient.get(`/getnextinjudgequeue/`, {
			params: {
				sessionid: SESSION_ID(),
				contestid: contestID,
				postid: currentQueueItemID,
				direction,
			},
		});
		return response.data;
	},

	/**
	 * Fetch a specific judge queue item by to-be-judged ID.
	 * Uses GET /getjudgingqueue/?tobejudgedid=<ID>&sessionid=<SESSION>.
	 * Backend returns: { result: { permitted, post?, contestID?, milestone_info?, judged?, remaining? } }
	 * or error if not found / not permitted.
	 */
	getJudgeQueueItemByTobeJudgedId: async (tobejudgedId) => {
		const response = await apiClient.get(`/getjudgingqueue/`, {
			params: {
				tobejudgedid: tobejudgedId,
				sessionid: SESSION_ID(),
			},
		});
		return response.data;
	},

	getContestAgenda: async (contestID, date) => {
		const response = await apiClient.post(`/getcontestagenda/`, {
			contestid: contestID,
			currenttime: date,
		});
		return response.data;
	},

	createContestAgendaItem: async (data) => {
		const response = await apiClient.post(`/createcontestagendaitem/`, data);
		return response.data;
	},

	updateContestAgendaItem: async (data) => {
		const response = await apiClient.post(`/updatecontestagendaitem/`, data);
		return response.data;
	},

	deleteContestAgendaItem: async (AgendaID) => {
		const response = await apiClient.post(`/deletecontestagendaitem/`, {
			id: AgendaID,
		});
		return response.data;
	},

	// TESTING ROUTE TODO: REMOVE
	populateContestAgenda: async (contestID) => {
		console.log("Populating agenda items!!!");
		const response = await apiClient.post(`/populatecontestagenda/`, {
			contestid: contestID,
		});
		return response.data;
	},

	getDashboard: async (contestID) => {
		const response = await apiClient.get(
			`/getdashboard/?contestID=${contestID}`
		);
		return response.data;
	},

	getInviteStatus: async (contestID) => {
		const response = await apiClient.get(
			`/getinvitestatus/?contestID=${contestID}`
		);
		return response.data;
	},

	addUserRole: async (contestID, roleType, MemberID = 0) => {
		const response = await apiClient.post(`/acceptinvite/`, {
			sessionid: SESSION_ID(),
			contestid: contestID,
			role: roleType,
			memberid: MemberID,
		});
		return response.data;
	},

	changeContestStatus: async (contestID, status) => {
		const response = await apiClient.post(`/changeconteststatus/`, {
			sessionid: SESSION_ID(),
			contestID,
			status,
		});
		return response.data;
	},

	createContest: async (contestName, MemberID) => {
		const response = await apiClient.post(`/createcontest/`, {
			memberid: MemberID,
			contestname: contestName,
		});
		return response.data;
	},

	/** Check/create default tutorial completion (same generic endpoints as deckpicker/questsalutes). */
	checkTutorialCompletion: async (memberID, name) => {
		const response = await apiClient.post(`/checktutorialcompletion/`, {
			memberID,
			name,
		});
		return response.data;
	},

	/** Mark tutorial complete (same generic endpoints as deckpicker/questsalutes). */
	updateTutorialCompletion: async (memberID, name, value) => {
		const response = await apiClient.post(`/updatetutorialcompletion/`, {
			memberID,
			name,
			value,
		});
		return response.data;
	},

	getGmCharadesProgress: async (contestId, adventureId) => {
		const path =
			API_URL && API_URL.replace(/\/$/, "").endsWith("goodglows")
				? "/gm/charades-progress/"
				: "/goodglows/gm/charades-progress/";
		const response = await apiClient.get(path, {
			params: { contest_id: contestId, adventure_id: adventureId },
		});
		return response.data?.result ?? response.data;
	},

	getGmMemberMilestones,
	gmUpdateMemberMilestone,
};
